// 3.0 interface routes. Every section is one main-nav entry with at most one
// row of page tabs beneath it — the two-level navigation the redesign holds to.
export const V3_ROUTES = {
  home: "/workspace/v3",
  analysis: "/workspace/v3/analysis",
  replay: "/workspace/v3/replay",
  compound: "/workspace/v3/compound",
  build: "/workspace/v3/workbench",
  model: "/workspace/v3/workbench/model",
  tracking: "/workspace/v3/workbench/tracking",
  gallery: "/workspace/v3/explore",
  recognize: "/workspace/v3/explore/recognize",
  theory: "/workspace/v3/explore/theory",
  journal: "/workspace/v3/space",
  profile: "/workspace/v3/space/profile",
  inbox: "/workspace/v3/space/inbox",
  settings: "/workspace/v3/space/settings",
} as const;

export type V3Section = "home" | "workbench" | "explore" | "space";

export function v3Section(pathname: string): V3Section {
  const segment = pathname.split("/")[3];
  if (segment === "workbench") return "workbench";
  if (segment === "explore") return "explore";
  if (segment === "space") return "space";
  return "home";
}

const LEGACY_WORKBENCH_PATHS: [string, string][] = [
  ["/workspace/workbench/model", V3_ROUTES.model],
  ["/workspace/workbench/monitor", V3_ROUTES.tracking],
  ["/workspace/workbench/build", V3_ROUTES.build],
];

/** Maps a 1.0 workbench href (shared helpers build those) onto 3.0. */
export function v3WorkbenchHref(href: string): string {
  const [path, query = ""] = href.split("?", 2);
  const match = LEGACY_WORKBENCH_PATHS.find(([legacy]) => path === legacy);
  const target = match ? match[1] : path;
  return query ? `${target}?${query}` : target;
}

/** Keeps workbench links inside 3.0 when the current page is a 3.0 page. */
export function workbenchHrefFrom(pathname: string, href: string): string {
  return pathname.startsWith(V3_ROUTES.home) ? v3WorkbenchHref(href) : href;
}

// 1.0 pages and the 3.0 page that replaces each for subscribers. 1.0 is
// admin-only; everyone else following an old link (emails, pushes,
// bookmarks) lands on the 3.0 counterpart.
const LEGACY_PAGES: [string, string][] = [
  ["/workspace", V3_ROUTES.home],
  ["/home", V3_ROUTES.home],
  ["/workspace/workbench/build", V3_ROUTES.build],
  ["/workspace/workbench/monitor", V3_ROUTES.tracking],
  ["/workspace/workbench/model", V3_ROUTES.model],
  ["/workspace/explore", V3_ROUTES.gallery],
  ["/workspace/explore/quiz", V3_ROUTES.theory],
  ["/workspace/explore/recognition", V3_ROUTES.recognize],
  ["/workspace/explore/surface", V3_ROUTES.recognize],
  ["/workspace/explore/selection", V3_ROUTES.gallery],
  ["/workspace/me/library", V3_ROUTES.journal],
  ["/workspace/me", V3_ROUTES.profile],
  ["/workspace/me/inbox", V3_ROUTES.inbox],
  ["/workspace/settings", V3_ROUTES.settings],
];

/**
 * Where a non-admin opening a 1.0 page goes instead, or null when the page
 * stays reachable (2.0, 3.0 and admin pages).
 */
export function v3PathForLegacy(pathname: string, search = ""): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (
    path.startsWith("/workspace/general") ||
    path.startsWith(V3_ROUTES.home) ||
    path.startsWith("/workspace/admin")
  )
    return null;
  const person = /^\/workspace\/explore\/people\/([^/]+)$/.exec(path);
  if (person) return `${V3_ROUTES.gallery}/people/${person[1]}`;
  const match = LEGACY_PAGES.find(([legacy]) => legacy === path);
  if (!match) return null;
  // Workbench links carry the workspace and strategy to open; keep them.
  const keepQuery = path.startsWith("/workspace/workbench/");
  return keepQuery && search ? `${match[1]}${search}` : match[1];
}

/** A link into 1.0, rewritten to its 3.0 page; other links pass through. */
export function toV3Href(href: string): string {
  const [path, query] = href.split("?", 2);
  return v3PathForLegacy(path, query ? `?${query}` : "") ?? href;
}
